import { createContext, useCallback, useContext, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { storage } from './storage'
import {
  InvalidCredentialsError,
  createPermalinkSession,
  deletePermalinkSession,
  logout as oauthLogout,
  spaLogin,
} from './oauthClient'

interface AuthState {
  token: string | null
  authLoading: boolean
  error: string | null
  login: (userId: string, password: string) => Promise<void>
  logout: () => void
}

const AuthCtx = createContext<AuthState | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [token, setToken] = useState<string | null>(() => storage.getAccessToken())
  const [authLoading, setAuthLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)

  const login = useCallback(async (userId: string, password: string) => {
    setError(null)
    setAuthLoading(true)
    try {
      await spaLogin(userId, password)
      const newToken = storage.getAccessToken()
      setToken(newToken)
      // Permalink Cookie session を発行 (失敗しても SPA は進む、spec/components/worker-facade/auth-routing.md)
      if (newToken) {
        void createPermalinkSession(newToken)
      }
    } catch (e: unknown) {
      if (e instanceof InvalidCredentialsError) {
        setError(e.message)
      } else if (e instanceof Error) {
        setError(e.message)
      } else {
        setError('Login failed')
      }
    } finally {
      setAuthLoading(false)
    }
  }, [])

  const logout = useCallback(() => {
    // permalink Cookie 破棄 (DELETE /session、Bearer 必須)
    const currentToken = storage.getAccessToken()
    if (currentToken) {
      void deletePermalinkSession(currentToken)
    }
    oauthLogout()
    setToken(null)
  }, [])

  const value = useMemo(
    () => ({ token, authLoading, error, login, logout }),
    [token, authLoading, error, login, logout]
  )

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthCtx)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}
